import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import TransitionPanel, { TRANSITION_DURATION } from '@aytm/app/stats/components/transition-panel/TransitionPanel'

import style from '@aytm/app/stats/page/question-views/components/items-list/styles/ItemsList.module.scss'

const Item = ({ id, collapsed, item, index, renderHeader, renderBody, onToggle }) => {
  // .collapsed drops the header shadow, which must stay while the body is still
  // animating shut -- so the marker lags the click by the panel's own duration.
  const [settled, setSettled] = useState(renderBody ? collapsed : true)

  useEffect(() => {
    if (collapsed) {
      const timer = setTimeout(() => setSettled(true), TRANSITION_DURATION)

      return () => clearTimeout(timer)
    }

    setSettled(false)
  }, [collapsed])

  return (
    <div id={id} className={classNames(style.item, { [style.collapsed]: settled })}>
      <div className={style.header} onClick={renderBody ? onToggle : null}>
        {renderHeader(item, { collapsed, index })}
      </div>
      {renderBody ? (
        <TransitionPanel className={classNames(style.body, { [style['body-collapsed']]: collapsed })} in={!collapsed}>
          {status => ('exited' === status ? null : renderBody(item, { index }))}
        </TransitionPanel>
      ) : null}
    </div>
  )
}

Item.propTypes = {
  id: PropTypes.string,
  collapsed: PropTypes.bool,
  item: PropTypes.any,
  index: PropTypes.number,
  renderHeader: PropTypes.func.isRequired,
  renderBody: PropTypes.func,
  onToggle: PropTypes.func.isRequired
}

Item.displayName = 'Item'

// Generic, reusable list of collapsible item rows. Question-agnostic and
// controlled: the set of open items (openKeys) lives with the caller so report-param
// sync / persistence stays out of here. The list owns only the collapse chrome and
// derives each row's open state; row header + body content are supplied via render
// props so conjoint, matrix, rapid-association, etc. can all reuse it.
const ItemsList = ({ items, itemId, itemKey, openKeys, renderHeader, renderBody, onToggle }) => {
  const openSet = useMemo(() => new Set((openKeys || []).map(String)), [openKeys])

  return (
    <>
      {items.map((item, index) => {
        const key = itemKey(item, index)
        const collapsed = !openSet.has(String(key))

        return (
          <Item
            key={key}
            id={itemId?.(item, index)}
            collapsed={collapsed}
            item={item}
            index={index}
            renderHeader={renderHeader}
            renderBody={renderBody}
            // second arg is the NEXT collapsed state: an open row collapses on click
            onToggle={() => onToggle(key, !collapsed)}
          />
        )
      })}
    </>
  )
}

ItemsList.propTypes = {
  items: PropTypes.array.isRequired,
  // (item, index) => the row's dom id, for the page to scroll to it by
  itemId: PropTypes.func,
  // returns the stable key for a row (e.g. its position); matched against openKeys
  itemKey: PropTypes.func.isRequired,
  // keys of the currently-open rows (controlled)
  openKeys: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  // (item, { collapsed, index }) => node — clickable row header content
  renderHeader: PropTypes.func.isRequired,
  // (item, { index }) => node — kept mounted; TransitionPanel animates it open/closed
  renderBody: PropTypes.func,
  // (key, willBeCollapsed) => void
  onToggle: PropTypes.func.isRequired
}

ItemsList.defaultProps = {
  openKeys: []
}

ItemsList.displayName = 'ItemsList'

export default ItemsList
